var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a, _b, _c, _d, _e, _f, _g;
import { observable, action } from 'mobx';
import { Graph, GreyCat } from '@greycat/core';
import * as ts from 'typescript';
import { serializable, object } from 'serializr';
import EditorStore from './EditorStore';
import ConsoleStore from './ConsoleStore';
import SidebarStore from './SidebarStore';
import SettingsStore from './SettingsStore';
import LayoutStore from './LayoutStore';
import ChartsStore from './ChartsStore';
const TS_OPTS = {
    compilerOptions: {
        target: ts.ScriptTarget.ES2015,
        module: ts.ModuleKind.CommonJS,
        removeComments: true,
        noImplicitUseStrict: true,
    },
};
export default class StudioStore {
    constructor() {
        this.contextIndex = 0;
        this._graph = GreyCat.newGraph();
        this._data = null;
        this._globalTypes = {};
        this._globalFunctions = {};
        this.console = new ConsoleStore();
        this.sidebar = new SidebarStore(this._graph);
        this.settings = new SettingsStore();
        this.layout = new LayoutStore();
        this._charts = new ChartsStore(this._graph, this.console);
        this.updateGlobalTypes();
        this.updateGlobalFunctions();
        if ('true' === process.env.TARGET_ELECTRON) {
            this._graph.addPlugin(new (require('@greycat/plugin-importer'))());
        }
    }
    execute() {
        const editor = this.sidebar.editorWorkspaces.current;
        if (editor) {
            const main = editor.files.get(EditorStore.MAIN);
            this.console.debug(`Executing: ${editor.name}/${main.uri}`);
            this._graph.newContext()
                .execute(this._graph.newFunction().parse(main.content), (ctx) => {
                // tslint:disable-next-line:no-console
                console.log('==> execute result', ctx.toObject());
                this.console.debug('Done.');
                this._data = ctx.toObject();
                this.updateGlobalTypes();
                this.updateGlobalFunctions();
            });
        }
    }
    interpretExtensions() {
        const editor = this.sidebar.editorWorkspaces.current;
        if (editor) {
            this.interpretExtension(editor);
        }
    }
    interpretExtension(ws) {
        const file = ws.files.get(EditorStore.EXTENSIONS);
        try {
            const { outputText } = ts.transpileModule(file.content, TS_OPTS);
            const extensionsLoader = new Function('studio', 'exports', outputText);
            const extensions = {};
            extensionsLoader({ console: this.console }, extensions);
            Object.keys(extensions).forEach((name) => {
                if (typeof extensions[name] === 'function') {
                    this._graph.declareFunction(name, extensions[name]);
                }
            });
            this.updateGlobalTypes();
            this.updateGlobalFunctions();
        }
        catch (err) {
            this.console.error(`[Extension Error][${ws.name}/${file.uri}] ${err.message}`);
        }
    }
    changeContextIndex(index) {
        this.contextIndex = index;
    }
    updateGlobalTypes() {
        this._globalTypes = JSON.parse(this._graph.exportTypes());
    }
    updateGlobalFunctions() {
        this._globalFunctions = JSON.parse(this._graph.exportFunctions()).reduce((o, t) => {
            o[t.name] = t;
            return o;
        }, {});
    }
    get graph() {
        return this._graph;
    }
    get data() {
        return this._data;
    }
    get globalTypes() {
        return this._globalTypes;
    }
    get globalFunctions() {
        return this._globalFunctions;
    }
    get charts() {
        return this._charts;
    }
}
StudioStore.KEY = 'greycat-studio';
__decorate([
    observable,
    serializable(object(ConsoleStore)),
    __metadata("design:type", typeof (_a = typeof ConsoleStore !== "undefined" && ConsoleStore) === "function" ? _a : Object)
], StudioStore.prototype, "console", void 0);
__decorate([
    observable,
    serializable(object(SidebarStore)),
    __metadata("design:type", typeof (_b = typeof SidebarStore !== "undefined" && SidebarStore) === "function" ? _b : Object)
], StudioStore.prototype, "sidebar", void 0);
__decorate([
    observable,
    serializable(object(SettingsStore)),
    __metadata("design:type", typeof (_c = typeof SettingsStore !== "undefined" && SettingsStore) === "function" ? _c : Object)
], StudioStore.prototype, "settings", void 0);
__decorate([
    observable,
    serializable(object(LayoutStore)),
    __metadata("design:type", typeof (_d = typeof LayoutStore !== "undefined" && LayoutStore) === "function" ? _d : Object)
], StudioStore.prototype, "layout", void 0);
__decorate([
    observable,
    serializable,
    __metadata("design:type", Number)
], StudioStore.prototype, "contextIndex", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_e = typeof Graph !== "undefined" && Graph) === "function" ? _e : Object)
], StudioStore.prototype, "_graph", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], StudioStore.prototype, "_data", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], StudioStore.prototype, "_globalTypes", void 0);
__decorate([
    observable,
    __metadata("design:type", Object)
], StudioStore.prototype, "_globalFunctions", void 0);
__decorate([
    observable,
    __metadata("design:type", typeof (_f = typeof ChartsStore !== "undefined" && ChartsStore) === "function" ? _f : Object)
], StudioStore.prototype, "_charts", void 0);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "execute", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "interpretExtensions", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [typeof (_g = typeof EditorStore !== "undefined" && EditorStore) === "function" ? _g : Object]),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "interpretExtension", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Number]),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "changeContextIndex", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "updateGlobalTypes", null);
__decorate([
    action.bound,
    __metadata("design:type", Function),
    __metadata("design:paramtypes", []),
    __metadata("design:returntype", void 0)
], StudioStore.prototype, "updateGlobalFunctions", null);
